package com.eladfinish.bible.contest.features.verseWidget

import com.eladfinish.bible.contest.analytics.AnalyticsConstants.VerseWidget
import com.eladfinish.bible.contest.domain.BooksRepository

/**
 * The one place that turns a [VerseWidgetConfig] into what is reported: the parameters that every widget
 * event carries, and the user properties. A setting is reported with the same name and the same values
 * wherever it is read (docs/analytics/verse-widget.md).
 */
object VerseWidgetAnalytics {

    /**
     * The settings as parameters of an event: `source` (mikud / book / parasha / all), `order`, `interval`
     * (minutes), `nikud` and `teamim` (true / false), and, only for a book, `book` (its number, 1..39) and
     * `book_name` (the English name, to read a report without a table). All are text, like the `value` of
     * `verse_widget_config_changed`.
     *
     * @param sourceKey the name of the `source` parameter: `verse_widget_screen_opened` already uses
     * `source` for the way the screen was reached.
     */
    fun params(config: VerseWidgetConfig, sourceKey: String = VerseWidget.PARAM_SOURCE): Map<String, Any> =
        buildMap {
            put(sourceKey, config.source.analyticsValue)
            if (config.source == VerseWidgetSource.BOOK) {
                put(VerseWidget.PARAM_BOOK, config.bookNumber.toString())
                put(VerseWidget.PARAM_BOOK_NAME, bookName(config))
            }
            put(VerseWidget.PARAM_ORDER, config.order.analyticsValue)
            put(VerseWidget.PARAM_INTERVAL, config.intervalMinutes.toString())
            put(VerseWidget.PARAM_NIKUD, config.showNikud.toString())
            put(VerseWidget.PARAM_TEAMIM, config.showTeamim.toString())
        }

    /**
     * The settings as Firebase user properties. The book is its English name, and `none` when the source is not
     * a book, so that an old choice of book does not stay on the user after the source changed.
     */
    fun userProperties(config: VerseWidgetConfig): Map<String, String> = mapOf(
        VerseWidget.USER_PROPERTY_SOURCE to config.source.analyticsValue,
        VerseWidget.USER_PROPERTY_BOOK to
                if (config.source == VerseWidgetSource.BOOK) bookName(config) else VerseWidget.BOOK_NONE,
        VerseWidget.USER_PROPERTY_ORDER to config.order.analyticsValue,
        VerseWidget.USER_PROPERTY_INTERVAL to config.intervalMinutes.toString(),
        VerseWidget.USER_PROPERTY_NIKUD to config.showNikud.toString(),
        VerseWidget.USER_PROPERTY_TEAMIM to config.showTeamim.toString(),
    )

    /** Whether every reported setting is still the one a new widget starts with. */
    fun isDefault(config: VerseWidgetConfig): Boolean = userProperties(config) == userProperties(VerseWidgetConfig())

    private fun bookName(config: VerseWidgetConfig): String = BooksRepository.bookNamesEnglish[config.bookNumber - 1]

    private val VerseWidgetSource.analyticsValue get() = name.lowercase()
    private val VerseWidgetOrder.analyticsValue get() = name.lowercase()
}
